<script lang="ts" setup>
import type { FavoriteCreateInput, FavoriteList } from '~/models/Favorites'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { faStar as faStarSolid } from '@fortawesome/pro-solid-svg-icons'
import { faClone, faEdit, faStar, faTrash } from '@fortawesome/duotone-regular-svg-icons'
import { AuToast } from '~/stores/toasts'
import { useFavorites } from '~/composables/useFavorites'
import Button from '~/components/ui/Button.vue'
import AuDialog from '~/components/ui/AuDialog.vue'
import EditFavoriteListDialog from '~/components/favorites/dialogs/EditFavoriteListDialog.vue'
import DuplicateFavoriteListDialog from '~/components/favorites/dialogs/DuplicateFavoriteListDialog.vue'
import DeleteFavoriteListDialog from '~/components/favorites/dialogs/DeleteFavoriteListDialog.vue'
import CreateFavoriteListDialog from '~/components/favorites/dialogs/CreateFavoriteListDialog.vue'
import AddToFavoriteDialog from '~/components/favorites/dialogs/AddToFavoriteDialog.vue'

const {
  item,
  action,
  disabled = false,
} = defineProps<{
  item?: FavoriteList | BaseItem
  action: 'addTo' | 'create' | 'delete' | 'edit' | 'duplicate'
  disabled?: boolean
}>()

const emit = defineEmits<{
  update: [item: FavoriteList]
  delete: [item: FavoriteList]
  create: [item: FavoriteList]
  addToList: [{ item: FavoriteCreateInput, list: FavoriteList }]
  duplicate: [item: FavoriteList]
}>()

const { t } = useI18n()
const { addItemToList, createFavoriteList, locationIds } = useFavorites()

const open = ref(false)
const isLoading = ref(false)

const isFavorite = computed(() => item && ('locationId' in item) && locationIds.value.includes(item.locationId))
const computedProps = computed(() => ({
  addTo: {
    title: t('favorite.addToList'),
    icon: isFavorite.value ? faStarSolid : faStar,
    dialogContent: AddToFavoriteDialog,
    activatorName: t('favorite.save'),
  },
  create: {
    title: t('favorite.createListDialogTitle'),
    icon: '',
    dialogContent: CreateFavoriteListDialog,
    activatorName: t('favorite.createListButtonName'),
  },
  delete: {
    title: t('favorite.deleteList'),
    icon: faTrash,
    dialogContent: DeleteFavoriteListDialog,
    activatorName: t('favorite.deleteList'),
  },
  edit: {
    title: t('favorite.editListDialogTitle'),
    icon: faEdit,
    dialogContent: EditFavoriteListDialog,
    activatorName: t('favorite.editListInfo'),
  },
  duplicate: {
    title: t('favorite.duplicateList'),
    dialogContent: DuplicateFavoriteListDialog,
    activatorName: t('favorite.duplicateList'),
    icon: faClone,
  }
})[action])

const updateList = (item: FavoriteList) => {
  emit('update', item)
  open.value = false
}

const createList = async (item: FavoriteList) => {
  isLoading.value = true
  try {
    emit('create', item)
    await createFavoriteList.mutateAsync(item)
    AuToast.success(t('favorite.createSuccess'))
    open.value = false
  } finally {
    isLoading.value = false
  }
}

const deleteList = (item: FavoriteList) => {
  emit('delete', item)
  open.value = false
}

const addToList = async ({ item, list }: { item: FavoriteCreateInput, list: FavoriteList }) => {
  isLoading.value = true
  try {
    emit('addToList', { item, list })
    await addItemToList.mutateAsync({ item, list })
    AuToast.success(t('favorite.addToListSuccess'))
    open.value = false
  } finally {
    isLoading.value = false
  }
}

const duplicateList = async (item: FavoriteList) => {
  open.value = false
  emit('duplicate', item)
}

const onActivate = (e?: Event) => {
  e?.preventDefault?.()
  e?.stopPropagation?.()
  open.value = true
}
</script>

<template>
  <div class="h-full">
    <slot
      name="activator"
      :on-activate="onActivate"
      :icon="computedProps.icon"
    >
      <Button
        :disabled="disabled"
        size="lg"
        variant="ghost"
        full-width
        @click="onActivate"
      >
        <font-awesome-icon :icon="computedProps.icon" />
        {{ computedProps.activatorName }}
      </Button>
    </slot>
    <AuDialog
      :open="open"
      :title="computedProps.title"
      @update:open="val => !val && (open = false)"
    >
      <template #content>
        <component
          :is="computedProps.dialogContent"
          :item="item"
          :is-loading="isLoading"
          @close="open = false"
          @update="updateList"
          @create="createList"
          @delete="deleteList"
          @add-to-list="addToList"
          @duplicate="duplicateList"
        />
      </template>
    </AuDialog>
  </div>
</template>
